import { FURNITURE_CONFIG } from '~/components/ThreeDModel/furnitureConfig'
import { computeSidePanelDimensions } from '~/utils/computeSidePanelDimensions'
import type { PanelPart } from '~/context/wardrobePartsContext'
import {
  workTableInnerWidth,
  workTableTopPanelSheet,
  workTableBackPanelSheet,
  workTableFrontPanelSheet,
  workTableBottomPanelSheet,
  workTableDrawerDimensions,
} from '~/utils/workTableGeometry'

const { panelThickness, panelSpacing, drawerInsidePanelsOffset } =
  FURNITURE_CONFIG

function drawerPanels(
  drawerWidth: number,
  drawerHeight: number,
  drawerDepth: number
): PanelPart[] {
  const innerHeight = drawerHeight - panelSpacing
  const innerWidth = drawerWidth - panelThickness * 2
  const innerDepth = drawerDepth - panelThickness * 2
  const sideHeight = innerHeight - drawerInsidePanelsOffset

  return [
    { width: drawerWidth, height: innerHeight, quantity: 1 }, // front
    { width: innerWidth, height: innerDepth, quantity: 1 }, // bottom
    { width: innerDepth, height: sideHeight, quantity: 2 }, // left + right
    { width: innerWidth, height: sideHeight, quantity: 1 }, // back
  ]
}

export interface WorkTablePartsArgs {
  width: number
  height: number
  depth: number
  hasDrawers: boolean
  drawerCount: number
}

/**
 * Enumerate every PAL panel the work-table 3D scene draws so the pricing
 * quote matches the model. Uses shared geometry utilities from
 * workTableGeometry.ts (single source of truth for both 3D and pricing).
 */
export function computeWorkTableParts(args: WorkTablePartsArgs): {
  panels: PanelPart[]
} {
  const { width, height, depth, hasDrawers, drawerCount } = args
  const innerWidth = workTableInnerWidth(width)
  const panels: PanelPart[] = []

  // Side panels (legs)
  const sides = computeSidePanelDimensions({
    width,
    height,
    depth,
    isNewCollection: false,
    isSingleNarrowColumn: false,
    isTwoSingleDoorsException: false,
    isMirrored: false,
  })
  panels.push(
    { ...sides.left, quantity: 1 },
    { ...sides.right, quantity: 1 }
  )

  // Top panel
  const top = workTableTopPanelSheet(innerWidth, depth)
  panels.push({ width: top.width, height: top.height, quantity: 1 })

  // Back apron panel
  const back = workTableBackPanelSheet(innerWidth)
  panels.push({ width: back.width, height: back.height, quantity: 1 })

  if (hasDrawers) {
    const dims = workTableDrawerDimensions(innerWidth, depth, drawerCount)
    for (let i = 0; i < drawerCount; i++) {
      panels.push(
        ...drawerPanels(dims.drawerWidth, dims.drawerHeight, dims.drawerDepth)
      )
    }
  } else {
    // Front modesty panel
    const front = workTableFrontPanelSheet(innerWidth)
    panels.push({ width: front.width, height: front.height, quantity: 1 })

    // Bottom shelf panel
    const bottom = workTableBottomPanelSheet(depth)
    panels.push({ width: bottom.width, height: bottom.height, quantity: 1 })
  }

  return { panels }
}
